import {
    AssetIssueCode,
    AssetPreview,
    BookingIssueCode,
    BookingPreview,
    Client,
    CreateAssetBody,
    ImportAsset,
    ImportBooking,
    ImportFile,
    ImportLine,
    ImportPreview,
    MakeBookingBody,
    MoneyInput,
} from "./types.ts";

export class PreviewError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "PreviewError";
    }
}

const AMOUNT_PATTERN = /^-?\d+(\.\d+)?$/;

export function eurosToCents(amount: MoneyInput): number {
    const text = String(amount).trim();
    if (!AMOUNT_PATTERN.test(text)) {
        throw new PreviewError(`Amount '${amount}' is not a valid number`);
    }
    const negative = text.startsWith("-");
    const [wholePart, fractionPart = ""] = text.replace("-", "").split(".");
    const paddedFraction = (fractionPart + "000").slice(0, 3);
    const wholeCents = BigInt(wholePart) * 100n + BigInt(paddedFraction.slice(0, 2));
    const roundUp = Number(paddedFraction[2]) >= 5;
    const cents = wholeCents + (roundUp ? 1n : 0n);
    return Number(negative ? -cents : cents);
}

export function buildBookingBody(booking: ImportBooking, clientId: string): MakeBookingBody {
    const currency = booking.currency ?? "EUR";
    const lines = (booking.lines ?? [])
        .map((line: ImportLine) => ({
            general_ledger_account_id: requireAccountId(line),
            debit_amount_cents: eurosToCents(line.debit ?? 0),
            credit_amount_cents: eurosToCents(line.credit ?? 0),
            currency,
            description: line.account_label ?? null,
            id: null,
            subledger_id: null,
        }))
        .filter((line) => line.debit_amount_cents !== 0 || line.credit_amount_cents !== 0);

    return {
        client_business_id: clientId,
        booking_date: booking.booking_date,
        audit_reason: booking.audit_reason ?? "",
        lines,
    };
}

function requireAccountId(line: ImportLine): string {
    if (!line.account_id) {
        throw new PreviewError("Line is missing 'account_id'");
    }
    return line.account_id;
}

export function buildAssetBody(asset: ImportAsset, clientId: string): CreateAssetBody {
    return {
        client_business_id: clientId,
        name: requireAssetName(asset),
        purchase_date: asset.purchase_date,
        purchase_value_cents: eurosToCents(asset.purchase_value),
        residual_value_cents: eurosToCents(asset.residual_value),
        economic_lifetime_months: Number(asset.economic_lifetime_months),
        balance_sheet_gl_account_id: asset.balance_sheet_gl_account_id,
        profit_and_loss_gl_account_id: asset.profit_and_loss_gl_account_id,
        subledger_id: null,
        document_id: null,
        expected_bill_version: null,
    };
}

function requireAssetName(asset: ImportAsset): string {
    if (!asset.name) {
        throw new PreviewError("Fixed asset is missing 'name'");
    }
    return asset.name;
}

export function parseImportFile(text: string): ImportFile {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch (error) {
        throw new PreviewError(`File is not valid JSON: ${(error as Error).message}`);
    }
    if (
        parsed === null ||
        typeof parsed !== "object" ||
        !Array.isArray((parsed as {bookings?: unknown}).bookings)
    ) {
        throw new PreviewError("File must be an object with a 'bookings' array");
    }
    return parsed as ImportFile;
}

function previewBooking(booking: ImportBooking, index: number, clientId: string): BookingPreview {
    const base = {
        index,
        businessName: booking.business_name ?? null,
        bookingDate: booking.booking_date,
        auditReason: booking.audit_reason ?? "",
    };
    try {
        const body = buildBookingBody(booking, clientId);
        const totalDebitCents = sumBy(body.lines, (line) => line.debit_amount_cents);
        const totalCreditCents = sumBy(body.lines, (line) => line.credit_amount_cents);
        const imbalanceCents = totalDebitCents - totalCreditCents;

        const issues: BookingIssueCode[] = [];
        if (!body.audit_reason.trim()) issues.push("MISSING_AUDIT_REASON");
        if (body.lines.length < 2) issues.push("TOO_FEW_LINES");
        if (imbalanceCents !== 0) issues.push("UNBALANCED");
        if (body.lines.some((line) => line.debit_amount_cents !== 0 && line.credit_amount_cents !== 0)) {
            issues.push("INVALID_LINE");
        }

        return {
            ...base,
            lineCount: body.lines.length,
            totalDebitCents,
            totalCreditCents,
            imbalanceCents,
            issues,
            errorDetail: null,
            blocking: issues.length > 0,
            body,
        };
    } catch (error) {
        return {
            ...base,
            lineCount: 0,
            totalDebitCents: 0,
            totalCreditCents: 0,
            imbalanceCents: 0,
            issues: ["PARSE_ERROR"],
            errorDetail: (error as Error).message,
            blocking: true,
            body: null,
        };
    }
}

function previewAsset(asset: ImportAsset, index: number, clientId: string, today: string): AssetPreview {
    try {
        const body = buildAssetBody(asset, clientId);
        const issues: AssetIssueCode[] = [];
        if (body.residual_value_cents > body.purchase_value_cents) {
            issues.push("RESIDUAL_EXCEEDS_PURCHASE");
        }
        if (body.purchase_date > today) issues.push("PURCHASE_DATE_IN_FUTURE");

        return {
            index,
            name: body.name,
            purchaseValueCents: body.purchase_value_cents,
            residualValueCents: body.residual_value_cents,
            economicLifetimeMonths: body.economic_lifetime_months,
            issues,
            errorDetail: null,
            blocking: issues.length > 0,
            body,
        };
    } catch (error) {
        return {
            index,
            name: asset.name ?? "",
            purchaseValueCents: 0,
            residualValueCents: 0,
            economicLifetimeMonths: 0,
            issues: ["PARSE_ERROR"],
            errorDetail: (error as Error).message,
            blocking: true,
            body: null,
        };
    }
}

export function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}

export function buildPreview(file: ImportFile, clientId: string, today: string = todayIso()): ImportPreview {
    const bookings = file.bookings.map((booking, index) => previewBooking(booking, index, clientId));
    const assets = file.bookings.flatMap((booking) => booking.fixed_assets ?? []);
    return {
        detectedName: detectName(file),
        bookings,
        assets: assets.map((asset, index) => previewAsset(asset, index, clientId, today)),
    };
}

function detectName(file: ImportFile): string | null {
    for (const booking of file.bookings) {
        if (booking.business_name) return booking.business_name.trim();
    }
    return null;
}

export function normalizeBusinessName(name: string): string {
    return name.split(/\s+/).join("").replace(/\./g, "").toLowerCase();
}

function stripLegalForm(key: string): string {
    for (const suffix of ["bv", "nv"]) {
        if (key.endsWith(suffix) && key.length > suffix.length) {
            return key.slice(0, -suffix.length);
        }
    }
    return key;
}

export function suggestClient(detectedName: string | null, clients: Client[]): Client | null {
    if (!detectedName) return null;
    const key = normalizeBusinessName(detectedName);
    const exact = clients.filter((client) => normalizeBusinessName(client.name) === key);
    if (exact.length === 1) return exact[0];

    const stripped = stripLegalForm(key);
    const fuzzy = clients.filter(
        (client) => stripLegalForm(normalizeBusinessName(client.name)) === stripped,
    );
    return fuzzy.length === 1 ? fuzzy[0] : null;
}

function sumBy<T>(items: T[], selector: (item: T) => number): number {
    return items.reduce((total, item) => total + selector(item), 0);
}
